import { useState } from "react"
import Papa from "papaparse"
import { useNavigate } from "react-router-dom"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"
import { CSVUploader } from "./bulk-import/CSVUploader"
import { ImportPreview } from "./bulk-import/ImportPreview"
import { ImportProgress } from "./bulk-import/ImportProgress"
import { ImportResults } from "./bulk-import/ImportResults"

interface ParsedUser {
  email: string
  firstName: string
  lastName: string
  middleName?: string
  mobileNumber?: string
  workPhone?: string
  rowNumber: number
  errors: string[]
}

interface ImportResultsData {
  total: number
  successful: number
  failed: number
  skipped: number
  details: Array<{
    email: string
    action: 'created' | 'skipped' | 'failed'
    error?: string
  }>
}

export function BulkImport() {
  const [file, setFile] = useState<File | null>(null)
  const [parsedData, setParsedData] = useState<ParsedUser[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [importResults, setImportResults] = useState<ImportResultsData | null>(null)
  const [currentStep, setCurrentStep] = useState<'upload' | 'preview' | 'processing' | 'results'>('upload')
  const { toast } = useToast()
  const navigate = useNavigate()

  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile)
    
    try {
      const text = await selectedFile.text()
      const parsed = parseCSV(text)
      setParsedData(parsed)
      setCurrentStep('preview')
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to parse CSV file'
      toast({
        title: "Parse Error",
        description: errorMessage,
        variant: "destructive"
      })
    }
  }

  const parseCSV = (text: string): ParsedUser[] => {
    // Use a real CSV parser. The old implementation used text.split('\n') and
    // lines[i].split(',') with no quote handling, so a value like "Smith, Jr."
    // shifted every subsequent column, and CRLF inside quoted fields broke rows.
    const result = Papa.parse<string[]>(text, {
      skipEmptyLines: 'greedy',
      newline: undefined, // auto-detect \n vs \r\n
    })

    const rows = (result.data || []).filter((r) => r.some((c) => (c ?? '').trim() !== ''))
    if (rows.length === 0) throw new Error('The file appears to be empty')

    const headers = rows[0].map((h) => (h ?? '').trim().toLowerCase())

    const requiredHeaders = ['email', 'first name', 'last name']
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h))
    
    if (missingHeaders.length > 0) {
      throw new Error(`Missing required columns: ${missingHeaders.join(', ')}`)
    }

    const users: ParsedUser[] = []
    
    for (let i = 1; i < rows.length; i++) {
      const values = rows[i].map((v) => (v ?? '').trim())
      const errors: string[] = []
      
      const email = values[headers.indexOf('email')] || ''
      const firstName = values[headers.indexOf('first name')] || ''
      const lastName = values[headers.indexOf('last name')] || ''
      const middleName = values[headers.indexOf('middle name')] || ''
      const mobileNumber = values[headers.indexOf('mobile number')] || ''
      const workPhone = values[headers.indexOf('work phone')] || ''

      if (!email || !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
        errors.push('Invalid email')
      }
      if (!firstName) errors.push('Missing first name')
      if (!lastName) errors.push('Missing last name')

      users.push({
        email,
        firstName,
        lastName,
        middleName: middleName || undefined,
        mobileNumber: mobileNumber || undefined,
        workPhone: workPhone || undefined,
        rowNumber: i + 1,
        errors
      })
    }

    return users
  }

  const handleImport = async () => {
    const validUsers = parsedData.filter(u => u.errors.length === 0)
    
    if (validUsers.length === 0) {
      toast({
        title: "No Valid Users",
        description: "All rows have errors. Please fix them before importing.",
        variant: "destructive"
      })
      return
    }

    setCurrentStep('processing')
    setIsProcessing(true)

    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser()

      const { data: importRecord, error: importError } = await supabase
        .from('import_history')
        .insert({
          file_name: file?.name || 'unknown.csv',
          total_rows: validUsers.length,
          status: 'pending',
          // Was omitted entirely, so all 12 existing rows show "Unknown".
          imported_by: currentUser?.id ?? null
        })
        .select()
        .single()

      if (importError) throw importError

      const { data: sessionData } = await supabase.auth.getSession()
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/bulk-import-users`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionData.session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            users: validUsers,
            importId: importRecord.id,
            fileName: file?.name
          })
        }
      )

      if (!response.ok) {
        const contentType = response.headers.get("content-type")
        if (!contentType?.includes("application/json")) {
          console.error('Edge function returned non-JSON response:', response.status)
          throw new Error(`Import failed with status ${response.status}`)
        }
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        console.error('Edge function error:', errorData)
        throw new Error(errorData.error || `Import failed with status ${response.status}`)
      }

      const results = await response.json()
      setImportResults(results.results)
      setCurrentStep('results')

      toast({
        title: "Import Complete",
        description: `Successfully imported ${results.results.successful} users.`
      })

    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      console.error('Import error:', error)
      toast({
        title: "Import Failed",
        description: errorMessage,
        variant: "destructive"
      })
      setCurrentStep('preview')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleReset = () => {
    setFile(null)
    setParsedData([])
    setImportResults(null)
    setCurrentStep('upload')
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-bold">Bulk Import Users</h2>
        <p className="text-muted-foreground">
          Import multiple users at once via CSV upload. Accounts are created without sending any email — send welcome invitations separately from the user list once you're ready.
        </p>
      </div>

      {currentStep === 'upload' && (
        <CSVUploader onFileSelect={handleFileSelect} />
      )}

      {currentStep === 'preview' && (
        <ImportPreview
          data={parsedData}
          fileName={file?.name || 'unknown.csv'}
          onImport={handleImport}
          onCancel={handleReset}
        />
      )}

      {currentStep === 'processing' && (
        <ImportProgress totalUsers={parsedData.filter(u => u.errors.length === 0).length} />
      )}

      {currentStep === 'results' && importResults && (
        <ImportResults
          results={importResults}
          onNewImport={handleReset}
          onViewHistory={() => navigate('/admin/import-history')}
        />
      )}
    </div>
  )
}
